import axios from "axios";

// Same rule the API applies on POST admin/news/upload-image: images only, no SVG
// (it can carry script), 10 MB at most. Checked here first so a wrong file never
// travels to the server.
export const MAX_NEWS_IMAGE_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export function validateNewsImage(file) {
  if (!file || !ALLOWED_TYPES.includes(file.type)) {
    return "Only JPG, PNG, WEBP or GIF images can be uploaded.";
  }
  if (file.size > MAX_NEWS_IMAGE_BYTES) {
    return "Images must be 10 MB or smaller.";
  }
  return null;
}

/**
 * uploadNewsImage(file, { apiBase, token, onProgress, signal }) -> stored URL.
 * The URL lives on the blob host the API's description sanitizer allows, so an
 * image uploaded here survives saving the post.
 */
export async function uploadNewsImage(file, { apiBase, token, onProgress, signal } = {}) {
  const problem = validateNewsImage(file);
  if (problem) throw new Error(problem);
  const body = new FormData();
  body.append("image", file);
  let response;
  try {
    // No Content-Type header: axios must add the multipart boundary itself.
    response = await axios.post(`${apiBase}admin/news/upload-image`, body, {
      headers: { "x-access-token": token },
      signal,
      onUploadProgress: onProgress,
    });
  } catch (error) {
    throw new Error(error?.response?.data?.message || error?.message || "Image upload failed");
  }
  const url = response?.data?.data?.url;
  if (!url) throw new Error("The server answered with no image URL.");
  return url;
}

/**
 * CKEditor 5 upload adapter (pass the returned function in config.extraPlugins).
 * The stock build ships the image-upload UI but no adapter, so "Upload image",
 * paste and drag-and-drop of image files did nothing before this.
 */
export function createNewsUploadAdapterPlugin({ apiBase, getToken }) {
  return function NewsUploadAdapterPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      return {
        upload: async () => {
          const file = await loader.file;
          const url = await uploadNewsImage(file, {
            apiBase,
            token: getToken(),
            signal: controller ? controller.signal : undefined,
            onProgress: (event) => {
              if (event && event.total) {
                loader.uploadTotal = event.total;
                loader.uploaded = event.loaded;
              }
            },
          });
          return { default: url };
        },
        abort: () => {
          if (controller) controller.abort();
        },
      };
    };
  };
}
